import { Navigate, RouteObject } from 'react-router-dom';
import UserFeaturesGuard from '~/guards/UserFeaturesGuard';
import { FeatureFlag, UserRole } from '~/types/company';
import { DocumentDirection } from '~/types/document';
import lazyImport from '~/utils/lazyImport';
import { invoicesProfileRoutes } from '../profile/routing/invoices-profile-routes';

const Invoices = lazyImport(() => import('~/views/invoices/Invoices'));
const Bills = lazyImport(() => import('~/views/invoices/Bills'));
const DeliveryNotes = lazyImport(() => import('~/views/invoices/DeliveryNotes'));
const InvoicesDashboard = lazyImport(() => import('~/views/invoices/dashboard/InvoicesDashboard'));
const InvoicesPreview = lazyImport(() => import('~/views/invoices/InvoicesPreview'));
const InvoicesInfo = lazyImport(() => import('~/views/invoices/InvoicesInfo'));
const Quotes = lazyImport(() => import('~/views/invoices/Quotes'));
const SendInvoice = lazyImport(() => import('~/views/invoices/SendInvoice'));
const Builder = lazyImport(() => import('~/views/invoices/builder/Builder'));
const ImportInvoices = lazyImport(() => import('~/views/invoices/import/ImportInvoices'));
const ImportInvoicePDF = lazyImport(() => import('~/views/invoices/import-pdf/ImportInvoicePDF'));
const FinalizeDocument = lazyImport(() => import('~/views/invoices/preview/FinalizeDocument'));
const Profile = lazyImport(() => import('~/views/invoices/profile/Profile'));
const ProductCatalog = lazyImport(() => import('~/views/invoices/ProductCatalog'));
const DocumentLineCostCenterAllocationPage = lazyImport(
  () => import('~/views/invoices/document-cost-center-allocations/DocumentLineCostCenterAllocationPage'),
);

export const invoicesRoutes: RouteObject[] = [
  {
    path: 'dashboard/*',
    element: <InvoicesDashboard />,
  },
  {
    path: 'received/*',
    element: <Invoices direction={DocumentDirection.received} />,
  },
  {
    path: 'issued/*',
    element: <Invoices direction={DocumentDirection.issued} />,
  },
  {
    path: 'bills/*',
    element: <Bills />,
  },
  {
    path: 'ddt/*',
    element: <DeliveryNotes />,
  },
  {
    path: 'quotes/*',
    element: <Quotes />,
  },
  {
    path: 'builder/:invoiceId?',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Invoicing]} whitelistedRoles={[UserRole.ENTREPRENEUR]}>
        <Builder />
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'builder/:invoiceId/finalize',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Invoicing]} whitelistedRoles={[UserRole.ENTREPRENEUR]}>
        <FinalizeDocument />
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'builder/:invoiceId/preview',
    element: <InvoicesPreview />,
  },
  {
    path: 'builder/:invoiceId/send',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Invoicing]} whitelistedRoles={[UserRole.ENTREPRENEUR]}>
        <SendInvoice />
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'import',
    element: <ImportInvoices />,
  },
  {
    path: 'import-pdf/:transactionId?',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Invoicing]} whitelistedRoles={[UserRole.ENTREPRENEUR]}>
        <ImportInvoicePDF />
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'profile/*',
    element: <Profile />,
    children: invoicesProfileRoutes,
  },
  {
    path: 'info',
    element: <InvoicesInfo />,
  },
  {
    path: 'product-catalog',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.Invoicing]} whitelistedRoles={[UserRole.ENTREPRENEUR]}>
        <ProductCatalog />
      </UserFeaturesGuard>
    ),
  },
  {
    path: 'documents/:documentId/cost-center-allocations',
    element: (
      <UserFeaturesGuard requiredFeatures={[FeatureFlag.SplitByCostCenter]}>
        <DocumentLineCostCenterAllocationPage />
      </UserFeaturesGuard>
    ),
  },
  {
    path: '*',
    element: <Navigate to="../dashboard" replace />,
  },
];
